import { routeRejectedManager } from "@/setup/routes/mill.routes";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Divider, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { RejectedManagerTable } from "./components/RejectedManagerTable";
import { RejectedBillet } from "./models/RejectedBillet";
import { getRejectedBillets } from "./repositories";

type useRejectedBilletsProps = {
	shiftId: number | undefined;
	onError: (error: Error) => void;
};

export const useRejectedBillets = ({
	shiftId,
	onError,
}: useRejectedBilletsProps) => {
	return useQuery<RejectedBillet[], Error>({
		queryKey: ["rejectedBillets", shiftId],
		queryFn: ({ signal }) =>
			getRejectedBillets({ shiftId: shiftId || 0 }, signal),
		onError: onError,
		enabled: !!shiftId,
	});
};

export const RejectedManagerPage = () => {
	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const { data: rejectedBillets, isLoading } = useRejectedBillets({
		shiftId: shiftInfo?.CurrentShiftId,
		onError: (e) => setError(e.message),
	});

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			const url = `${routeRejectedManager}/${shiftId || Number(shiftIdParam)}`;
			navigate(url, { replace: true });
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				await getShiftDataFromAPI(null, shiftId);
			})();
		} else {
			(async () => {
				await getShiftDataFromAPI(null, 0);
			})();
		}
	}, []);

	return (
		<>
			<HelmetDexteel title="Rejected Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{
					backgroundColor: pageBgColor,
					minHeight: "calc(100vh - 160px)",
				}}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Rejected Manager
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Grid2
						container
						justifyContent="flex-start"
						alignItems="center"
						spacing={2}
					>
						<Grid2 maxWidth={"fit-content"}>
							<ShiftDayNavigatorControl preventInit />
						</Grid2>
					</Grid2>
				</Grid2>
				<Grid2>
					<Divider />
				</Grid2>
				<Grid2
					size={{ md: 12 }}
					sx={{
						height: "70vh",
						minHeight: "70vh",
					}}
					component={Paper}
					elevation={2}
				>
					<RejectedManagerTable
						rows={rejectedBillets || []}
						isLoading={isLoading}
						shiftId={shiftInfo?.CurrentShiftId}
						onGridReady={(api) => setGridApi(api)}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
